import { ApiResponse, CreateVideoRequest, VideoRequest, CostStats, ProviderInfo, CostEstimate, Provider, ImagePreview } from '../types/video';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log(`API Request: ${options.method || 'GET'} ${url}`);

      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private transformVideoRequest(video: any): VideoRequest {
    return {
      ...video,
      createdAt: new Date(video.createdAt),
      updatedAt: new Date(video.updatedAt),
    };
  }

  async createVideoRequest(request: CreateVideoRequest): Promise<ApiResponse<VideoRequest>> {
    const response = await this.request<VideoRequest>('/videos', {
      method: 'POST',
      body: JSON.stringify(request),
    });

    if (response.success && response.data) {
      response.data = this.transformVideoRequest(response.data);
    }

    return response;
  }

  async createImagePreview(request: {
    scenarioDescription: string;
    settings: CreateVideoRequest['settings'];
    quality?: 'fast' | 'quality';
    provider?: Provider;
  }): Promise<ApiResponse<ImagePreview>> {
    return this.request<ImagePreview>('/previews', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getVideoRequest(id: string): Promise<ApiResponse<VideoRequest>> {
    const response = await this.request<VideoRequest>(`/videos/${id}`);

    if (response.success && response.data) {
      response.data = this.transformVideoRequest(response.data);
    }

    return response;
  }

  async getVideoHistory(limit: number = 20): Promise<ApiResponse<VideoRequest[]>> {
    const response = await this.request<VideoRequest[]>(`/videos?limit=${limit}`);

    if (response.success && response.data) {
      response.data = response.data.map(video => this.transformVideoRequest(video));
    }

    return response;
  }

  async getCostStats(): Promise<ApiResponse<CostStats>> {
    return this.request<CostStats>('/videos/stats/costs');
  }

  async getProviderInfo(): Promise<ApiResponse<ProviderInfo>> {
    return this.request<ProviderInfo>('/videos/provider-info');
  }

  async getEstimatedCost(
    provider: Provider,
    duration: number,
    quality: 'fast' | 'quality'
  ): Promise<ApiResponse<CostEstimate>> {
    return this.request<CostEstimate>(
      `/videos/cost-estimate?provider=${provider}&duration=${duration}&quality=${quality}`
    );
  }

  async pollVideoStatus(
    id: string,
    onUpdate: (video: VideoRequest) => void,
    intervalMs: number = 2000,
    maxAttempts: number = 300 // 10 minutes max
  ): Promise<void> {
    let attempts = 0;

    const poll = async () => {
      attempts++;

      try {
        const response = await this.getVideoRequest(id);

        if (response.success && response.data) {
          onUpdate(response.data);

          // Stop polling if video is completed or failed
          if (response.data.status === 'completed' || response.data.status === 'failed') {
            return;
          }

          // Continue polling if not max attempts reached
          if (attempts < maxAttempts) {
            setTimeout(poll, intervalMs);
          }
        }
      } catch (error) {
        console.error('Error polling video status:', error);

        // Continue polling unless max attempts reached
        if (attempts < maxAttempts) {
          setTimeout(poll, intervalMs);
        }
      }
    };

    // Start polling
    setTimeout(poll, intervalMs);
  }
}

export const apiService = new ApiService();
